import { PLAYABLE_AREA } from './constants';
import { RoomState } from '../shared/sharedTypes';
import SocketClient from './socket-client';

class Chat {
    private socketClient: SocketClient;
    public chatBox: HTMLDivElement | null;
    private chatInput: HTMLTextAreaElement | null;
    private chatMessages: HTMLDivElement | null;
    private isChatVisible: boolean;
    private roomState: RoomState;

    constructor(bootstrapRoomState: RoomState, socketClient: SocketClient) {
        this.socketClient = socketClient;
        this.chatBox = null;
        this.chatInput = null;
        this.chatMessages = null;
        this.isChatVisible = true;
        this.roomState = bootstrapRoomState;

        this.createChatUI();
        this.setupEventListeners();
        this.autoResizeTextarea();

        this.handleViewportChange();
    }

    private createChatUI() {
        this.chatBox = document.createElement('div');
        this.chatBox.id = 'chat-box';
        this.chatBox.className = 'golfup-chat';
        this.chatBox.innerHTML = `
            <div class="golfup-chat__inner">
                <div class="golfup-chat__shadow"></div>
                <div class="golfup-chat__panel">
                    <div class="golfup-chat__messages" id="chat-messages"></div>
                    <textarea id="chat-input" rows="1" class="golfup-chat__input" placeholder="Press Enter to Chat..."></textarea>
                    <div class="golfup-chat__hint">SEND ⏎</div>
                </div>
            </div>
        `;

        this.chatMessages = this.chatBox.querySelector('#chat-messages') as HTMLDivElement;
        this.chatInput = this.chatBox.querySelector('#chat-input') as HTMLTextAreaElement;

        document.body.appendChild(this.chatBox);
    }

    private setupEventListeners() {
        if (this.chatInput) {
            this.chatInput.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (this.chatInput && this.chatInput.value.trim() !== '') {
                        this.sendMessage(this.chatInput.value);
                        this.chatInput.value = '';
                        this.autoResizeTextarea();
                    }
                }
            });

            this.chatInput.addEventListener('input', () => this.autoResizeTextarea());
            this.chatInput.addEventListener('focus', () => {
                this.chatBox?.classList.add('chat-expanded');
                this.autoResizeTextarea();
            });
            this.chatInput.addEventListener('blur', () => {
                this.chatBox?.classList.remove('chat-expanded');
            });
        }

        this.socketClient.onWebRTCMessage = (data: any) => {
            if (data.type === 'chatMessage') {
                this.displayMessage(data.data.name, data.data.message);
            }
        };

        this.socketClient.socket.on('playerJoined', (playerData: any) => {
            this.displaySystemMessage(`${playerData.name} joined the game.`);
        });

        this.socketClient.socket.on('playerLeft', (playerId: string, name: string) => {
            const playerName = name || 'A player';
            this.displaySystemMessage(`${playerName} left the game.`);
        });

        // roundEnded event
        this.socketClient.socket.on('roundEnded', (data: any) => {
            this.displaySystemMessage(`Round over! Level regenerating in ${data.timeUntilNextRound / 1000} seconds...`);
        });

        window.addEventListener('resize', this.handleViewportChange.bind(this));
    }

    private sendMessage(message: string) {
        const chatData = {
            type: 'chatMessage',
            data: {
                name: this.socketClient.name,
                message: message
            }
        };
        this.socketClient.sendToPeers(chatData);
        // Display the message locally as well
        this.displayMessage(this.socketClient.name, message);
    }

    public displayMessage(name: string, message: string) {
        if (this.chatMessages) {
            const messageElement = document.createElement('p');
            messageElement.innerHTML = `<strong>${name}:</strong> ${message}`;
            this.chatMessages.appendChild(messageElement);
            this.chatMessages.scrollTop = this.chatMessages.scrollHeight;
        }
    }

    public displaySystemMessage(message: string) {
        if (this.chatMessages) {
            const messageElement = document.createElement('p');
            messageElement.innerHTML = `<em>${message}</em>`;
            messageElement.style.color = '#fff9';
            this.chatMessages.appendChild(messageElement);
            this.chatMessages.scrollTop = this.chatMessages.scrollHeight;
        }
    }

    public toggleChatInputFocus() {
        if (!this.isChatVisible) {
            if (this.chatBox) this.chatBox.style.display = 'block';
            this.isChatVisible = true;
        }
        this.chatInput?.focus();
    }

    private autoResizeTextarea() {
        if (!this.chatInput) return;
        this.chatInput.style.height = 'auto';
        const newHeight = Math.min(this.chatInput.scrollHeight, 280);
        this.chatInput.style.height = `${Math.max(newHeight, 56)}px`;
    }

    private handleViewportChange() {
        if (window.innerWidth < PLAYABLE_AREA + 320) {
            if (this.chatBox) this.chatBox.style.display = 'none';
            this.isChatVisible = false;
        } else {
            if (this.chatBox) this.chatBox.style.display = 'block';
            this.isChatVisible = true;
        }
    }
}

export default Chat;
